import { useState } from 'react';
import { ThumbsUp, AlertCircle } from 'lucide-react';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/utils/utils';
import { QuestionShell } from '../QuestionShell';
import { describeStrings } from '../../utils/describe.strings';
import type { OutcomeAnswer, OutcomeResult } from '../../types/describe.types';

interface OutcomeQuestionProps {
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  answer: OutcomeAnswer | null;
  onSubmit: (answer: OutcomeAnswer) => void;
}

export const OutcomeQuestion = ({
  status,
  isLast,
  answer,
  onSubmit,
}: OutcomeQuestionProps) => {
  if (status === 'answered' && answer) {
    return (
      <QuestionShell
        kind="core"
        stepNumber={2}
        heading={`2. ${describeStrings.outcome.heading}`}
        status="answered"
        isLast={isLast}
      >
        <OutcomeAnsweredView answer={answer} />
      </QuestionShell>
    );
  }

  return (
    <QuestionShell
      kind="core"
      stepNumber={2}
      heading={describeStrings.outcome.heading}
      subheading={describeStrings.outcome.subheading}
      status={status}
      isLast={isLast}
    >
      <OutcomeEditor initial={answer} onSubmit={onSubmit} />
    </QuestionShell>
  );
};

function OutcomeEditor({
  initial,
  onSubmit,
}: {
  initial: OutcomeAnswer | null;
  onSubmit: (answer: OutcomeAnswer) => void;
}) {
  const [result, setResult] = useState<OutcomeResult | null>(
    initial?.result ?? null
  );
  const [context, setContext] = useState(initial?.context ?? '');

  const submit = () => {
    if (!result || !context.trim()) return;
    onSubmit({ result, context: context.trim() });
  };

  const handleContextKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="p-5 bg-white border border-slate-200 shadow-sm rounded-2xl w-full">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <OutcomeOption
          icon={<ThumbsUp className="h-5 w-5" aria-hidden />}
          label={describeStrings.outcome.achieved}
          selected={result === 'achieved'}
          tone="positive"
          onClick={() => setResult('achieved')}
        />
        <OutcomeOption
          icon={<AlertCircle className="h-5 w-5" aria-hidden />}
          label={describeStrings.outcome.notAchieved}
          selected={result === 'not_achieved'}
          tone="negative"
          onClick={() => setResult('not_achieved')}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-900 mb-1.5 uppercase tracking-wide">
          {describeStrings.outcome.contextLabel}
        </label>
        <Textarea
          value={context}
          onChange={(event) => setContext(event.target.value)}
          onKeyDown={handleContextKeyDown}
          placeholder={describeStrings.outcome.contextPlaceholder}
          rows={3}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 resize-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary/60"
          disabled={!result}
        />
        {result && (
          <div className="flex justify-end mt-3">
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={!context.trim()}
            >
              {describeStrings.common.continue}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface OutcomeOptionProps {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  tone: 'positive' | 'negative';
  onClick: () => void;
}

function OutcomeOption({
  icon,
  label,
  selected,
  tone,
  onClick,
}: OutcomeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors',
        selected
          ? 'border-2 border-slate-900 bg-slate-900 text-white shadow-sm'
          : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-white hover:border-slate-300'
      )}
      aria-pressed={selected}
    >
      <span
        className={cn(
          'flex items-center',
          selected && tone === 'positive' && 'text-emerald-300',
          selected && tone === 'negative' && 'text-red-300',
          !selected && 'text-slate-400'
        )}
      >
        {icon}
      </span>
      {label}
    </button>
  );
}

function OutcomeAnsweredView({ answer }: { answer: OutcomeAnswer }) {
  const isAchieved = answer.result === 'achieved';
  return (
    <div className="space-y-3 max-w-md">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 shadow-sm rounded-lg text-sm font-medium text-slate-900">
        {isAchieved ? (
          <ThumbsUp className="h-4 w-4 text-emerald-500" aria-hidden />
        ) : (
          <AlertCircle className="h-4 w-4 text-red-400" aria-hidden />
        )}
        {isAchieved
          ? describeStrings.outcome.achieved
          : describeStrings.outcome.notAchieved}
      </div>
      {answer.context && (
        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed">
          {answer.context}
        </div>
      )}
    </div>
  );
}
